import { Suspense, lazy, type ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import { RedirectIfAuthenticated, RequireAuth } from '@web/auth/guards';
import { ActivatePage, ForgotPasswordPage, ResetPasswordPage } from '@web/auth/pages/AccountPages';
import { routesFor } from '@web/modules/registry';
import type { RouteHandle } from '@web/modules/types';
import { PageLoader } from '@web/ui/PageLoader';
import { NotFoundState } from '@web/ui/StatusPages';
import { RouteError } from './common/RouteError';
import type { ShellKind } from './detect';

const LoginPage = lazy(() => import('@web/auth/pages/LoginPage'));
const VerifyPage = lazy(() => import('@web/auth/pages/VerifyPage'));
const AdminShell = lazy(() => import('./admin/AdminShell'));
const AdminHome = lazy(() => import('./admin/AdminHome'));

const s = (node: ReactNode, fullScreen = false) => <Suspense fallback={<PageLoader fullScreen={fullScreen} />}>{node}</Suspense>;

/** Full route tree of one shell (auth pages + shell layout with module routes). */
export function buildRoutes(shell: ShellKind): RouteObject[] {
  const moduleRoutes = routesFor(shell);
  const home: RouteHandle = { crumb: 'common:nav.home' };

  const publicRoutes: RouteObject[] = [
    {
      path: 'login',
      element: <RedirectIfAuthenticated>{s(<LoginPage />, true)}</RedirectIfAuthenticated>,
    },
    { path: 'activate', element: <ActivatePage /> },
    { path: 'forgot-password', element: <ForgotPasswordPage /> },
    { path: 'reset-password', element: <ResetPasswordPage /> },
    // public QR verification of printed documents
    { path: 'verify/:kind/:code', element: s(<VerifyPage />, true) },
  ];

  // one shell: the admin app (the theme and shell types keep the other kinds for shared code)
  const layout: ReactNode = <AdminShell />;
  const index: ReactNode = <AdminHome />;

  return [
    ...publicRoutes,
    {
      path: '/',
      element: <RequireAuth>{s(layout, true)}</RequireAuth>,
      errorElement: <RouteError />,
      handle: home,
      children: [
        { index: true, element: s(index) },
        ...moduleRoutes,
        { path: '*', element: <NotFoundState /> },
      ],
    },
  ];
}
